/**
 * Hall of Fame (`/hof`) data: the win-rate ranking and the human dice-luck
 * records. Screens render these view models; the logic lives here so it can be
 * tested without rendering.
 */
import type { Href } from 'expo-router'
import type {
  DiceLuckLeaderboardGame,
  DiceLuckLeaderboardOverall,
  DiceLuckLeaderboardResponse,
} from '@warcarp/api-types'
import { apiGet } from './api'
import { formatDiceLuckScore, formatDiceLuckSwing } from './diceLuck'
import { fitStencilSize, stencilWidthEm } from './stencilText'

/** Games a player needs before they appear in the win-rate ranking. */
export const HOF_MIN_GAMES = 5
/** Rows in the Hall of Fame win-rate preview: 5 on phones, 10 in two columns on tablet and desktop. */
export const HOF_PREVIEW_COUNT = 5
export const HOF_WIDE_PREVIEW_COUNT = 10
/** Players per page on the full win-rate list. */
export const HOF_PAGE_SIZE = 25
/** Readable cap for Hall of Fame pages on wide windows (see lib/pageLayout). */
export const HOF_MAX_WIDTH = 880

export interface WinRateEntry {
  rank: number
  id: string
  username: string
  displayName: string
  stats: { gamesPlayed: number; wins: number; losses: number; winPct: number }
}

export interface WinRateResponse {
  minGames: number
  /** Every qualifying player (servers before paging omit it). */
  total?: number
  offset?: number
  entries: WinRateEntry[]
}

export function winRateQuery(limit: number, offset = 0) {
  return {
    queryKey: ['leaderboard', 'win-rate', limit, offset, HOF_MIN_GAMES] as const,
    queryFn: () => apiGet<WinRateResponse>(`/api/v1/leaderboard?limit=${limit}&offset=${offset}&minGames=${HOF_MIN_GAMES}`),
  }
}

/** Number of pages for the full list; at least one so an empty list still renders. */
export function winRatePageCount(total: number | undefined, pageSize = HOF_PAGE_SIZE): number {
  return Math.max(1, Math.ceil((total ?? 0) / pageSize))
}

/**
 * Split preview rows into columns that read top-to-bottom, then left-to-right
 * (ranks 1–5 in the first column, 6–10 in the second), which is how a ranked
 * list is scanned.
 */
export function winRateColumns<T>(entries: readonly T[], columns: number): T[][] {
  const count = Math.max(1, Math.floor(columns))
  const perColumn = Math.ceil(entries.length / count)
  return Array.from({ length: count }, (_, i) => entries.slice(i * perColumn, (i + 1) * perColumn)).filter(column => column.length > 0)
}

export const diceLuckRecordsQuery = {
  queryKey: ['leaderboard', 'dice-luck'] as const,
  queryFn: () => apiGet<DiceLuckLeaderboardResponse>('/api/v1/leaderboard/dice-luck'),
}

export function formatWinPct(n: number): string {
  if (n <= 0) return '0%'
  return `${Math.round(n * 1000) / 10}%`
}

export type LuckRecordTone = 'lucky' | 'unlucky'

export interface LuckRecordBoxModel {
  title: string
  tone: LuckRecordTone
  name: string
  /** Luck Score shown beside the name, e.g. "129". */
  score: string
  /** Colour of the score: above 100 is lucky, below is unlucky. */
  scoreTone: LuckRecordTone | 'neutral'
  /** Net army swing shown beside the score, e.g. "+16.6 armies". */
  armies: string
  /** Game: "Sep 4, 2026". Player: "12 games · 279 rolls". */
  detail: string
  cta: string
  href: Href
  accessibilityLabel: string
  testID: string
}

function luckScore(record: { zScore: number; rollCount: number }): Pick<LuckRecordBoxModel, 'score' | 'scoreTone'> {
  return {
    score: formatDiceLuckScore(record.zScore, record.rollCount),
    scoreTone: record.zScore > 0 ? 'lucky' : record.zScore < 0 ? 'unlucky' : 'neutral',
  }
}

function armies(netSwing: number): string {
  return `${formatDiceLuckSwing(netSwing)} armies`
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

/** One player's single luckiest or unluckiest ranked game. Opens the board. */
export function luckGameBox(record: DiceLuckLeaderboardGame, tone: LuckRecordTone): LuckRecordBoxModel {
  const title = tone === 'lucky' ? 'Luckiest game' : 'Unluckiest game'
  return {
    title,
    tone,
    name: record.player.displayName,
    ...luckScore(record),
    armies: armies(record.netSwing),
    detail: shortDate(record.completedAt),
    cta: 'View game →',
    href: `/(app)/games/${record.gameId}`,
    accessibilityLabel: `${title}: ${record.player.displayName}, luck score ${formatDiceLuckScore(record.zScore, record.rollCount)}, in ${record.gameName ?? 'an untitled game'}. View game`,
    testID: tone === 'lucky' ? 'hof-luckiest-game' : 'hof-unluckiest-game',
  }
}

/** One player's lifetime luck across ranked games. Opens their profile. */
export function luckPlayerBox(record: DiceLuckLeaderboardOverall, tone: LuckRecordTone): LuckRecordBoxModel {
  const title = tone === 'lucky' ? 'Luckiest player' : 'Unluckiest player'
  return {
    title,
    tone,
    name: record.player.displayName,
    ...luckScore(record),
    armies: armies(record.netSwing),
    detail: `${record.gamesRecorded} games · ${record.rollCount} rolls`,
    cta: 'View profile →',
    href: `/(app)/u/${record.player.username}`,
    accessibilityLabel: `${title}: ${record.player.displayName}, luck score ${formatDiceLuckScore(record.zScore, record.rollCount)}. View profile`,
    testID: tone === 'lucky' ? 'hof-luckiest-player' : 'hof-unluckiest-player',
  }
}

/** Font sizes for a dice-luck record card, fitted to its measured content width. */
export interface LuckBoxSizes {
  /** Title (e.g. "UNLUCKIEST PLAYER") stays on one line: 10px down to 8.5px
   *  at the usual wide tracking; on very narrow cards (iPhone SE) the tracking
   *  tightens and it may go to 7.5px. */
  title: number
  /** Title letter spacing in px (tracking-widest is 0.1em). */
  titleLetterSpacing: number
  /** Name: 16px down to 12px, then wraps to a second line (never cut off). */
  name: number
  /** Score beside the army swing. */
  score: number
  /** Army swing shares the score's line: 12px down to 9px. */
  armies: number
}

export const LUCK_BOX_SCORE_SIZE = 14
const TITLE_TRACKING_EM = 0.1 // tracking-widest
const TITLE_TIGHT_TRACKING_EM = 0.04
const SCORE_ARMIES_GAP = 6

export function luckBoxSizes(model: Pick<LuckRecordBoxModel, 'title' | 'name' | 'score' | 'armies'>, width: number): LuckBoxSizes {
  const scoreWidth = stencilWidthEm(model.score) * LUCK_BOX_SCORE_SIZE
  const title = model.title.toUpperCase()
  const wide = fitStencilSize(title, width, 10, 8.5, TITLE_TRACKING_EM)
  const fitsWide = !(width > 0) || stencilWidthEm(title, TITLE_TRACKING_EM) * wide <= width * 0.96
  const titleSize = fitsWide ? wide : fitStencilSize(title, width, 9.5, 7.5, TITLE_TIGHT_TRACKING_EM)
  return {
    title: titleSize,
    titleLetterSpacing: titleSize * (fitsWide ? TITLE_TRACKING_EM : TITLE_TIGHT_TRACKING_EM),
    name: fitStencilSize(model.name, width, 16, 12),
    score: LUCK_BOX_SCORE_SIZE,
    armies: fitStencilSize(model.armies, width - scoreWidth - SCORE_ARMIES_GAP, 12, 9),
  }
}

